import {
  Typography,
  makeStyles,
  Button,
  FeatureChip,
  Grid,
  Hidden,
  IconButton,
  Link,
  useIsMobile,
  useMediaQuery
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { DropdownSelect, useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CancelIcon from '@material-ui/icons/Cancel'
import MenuIcon from '@material-ui/icons/Menu'
import { useSearchLocationsQuery } from '@paintscout/api'
import { getFeature, getFeatures, getObjectLabels } from '@paintscout/util/builder'
import { BackLink } from '@paintscout/react-quote'
import classnames from 'classnames'
import find from 'lodash/find'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import pageMargins from 'src/components/styles/snippets/pageMargins'
import striptags from 'striptags'
import { useFlags } from 'shared/components/common/FlagsProvider'
import MultiTabChecker from '../MultiTabChecker'

const SPACE_BETWEEN = 'space-between'
const TRIAL_BANNER_CLASS = 'trial-banner'
const BILLING_PLAN_PATH = '/settings?section=billing-plan'
import DropdownNewButton from '../DropdownNewButton'
import UserButtons from './UserButtons'
import { useNavContext } from './SideNav'

const useStyles = makeStyles<Theme, { isMasquerading?: boolean }>(
  (theme) => ({
    root: ({ isMasquerading }) => ({
      boxShadow: 'none',
      borderBottom: `1px solid ${theme.paintscout.palette.brand?.Neutral?.P90}`,
      borderTop: !isMasquerading ? `1px solid ${theme.paintscout.palette.brand?.Neutral?.P90}` : 'none',
      // height: theme.paintscout.header.height.md,
      position: 'relative',
      zIndex: 3,
      [theme.breakpoints.down('sm')]: {
        // height: theme.paintscout.header.height.sm
      },
      '@media print': {
        display: 'none'
      }
    }),
    headerContentWrapper: {
      background: theme.palette.secondary.main,
      transition: `${theme.transitions.duration.standard}ms background`
    },
    headerContent: {
      ...pageMargins(theme, 'xl'),
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      alignItems: 'center',
      boxSizing: 'content-box',
      // height: theme.paintscout.header.height.md,
      [theme.breakpoints.down('sm')]: {
        // height: theme.paintscout.header.height.sm,
        paddingTop: 'env(safe-area-inset-top)'
      },
      [theme.breakpoints.down('xs')]: {
        width: '96%'
      }
    },
    headerContentChild: {
      display: 'flex',
      alignItems: 'center',
      columnGap: theme.spacing(1)
    },
    companyLogo: {
      height: theme.typography.pxToRem(50),
      width: 'auto'
    },
    companyLogoNew: {
      marginTop: theme.spacing(0.5)
    },
    drawerButton: {
      color: theme.paintscout.palette.brand?.Neutral?.P40 || theme.palette.secondary.contrastText
    },
    userButtons: {},
    trialBannerWrapper: {
      position: 'relative',
      zIndex: 3,
      backgroundColor: theme.paintscout.palette.secondary?.['260']?.C95
    },
    paymentBannerWrapper: {
      backgroundColor: theme.palette.error.light,
      borderBottom: '1px solid rgba(0, 0, 0, .12)'
    },
    banner: {
      ...pageMargins(theme, 'xl'),
      padding: theme.spacing(2, 0)
    },
    trialBanner: {
      columnGap: theme.spacing(6),
      minHeight: '60px',
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
        textAlign: 'center',
        justifyContent: 'center',
        columnGap: 32
      },
      rowGap: 12,
      color: theme.palette.text.primary,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        marginRight: '15px'
      },
      '& p': {
        lineHeight: 1.5
      },
      '& button': {
        // marginLeft: '15px',
        // for some reason without this the buttons horizontal padding is overlapped by the text
        minWidth: 'auto',
        [theme.breakpoints.down('xs')]: {
          // margin: '10px auto 0 auto'
        }
      },
      '&$trialBanner': {
        padding: theme.spacing(1, 0)
      }
    },
    trialBannerActions: {
      // marginLeft: theme.spacing(6)
    },
    paymentBanner: {
      [theme.breakpoints.down('md')]: {
        flexWrap: 'wrap',
        textAlign: 'center',
        justifyContent: 'center'
      },
      color: theme.palette.error.dark,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      '& svg': {
        marginRight: '15px'
      },
      '& p': {
        lineHeight: 1.5
      },
      '& button': {
        marginLeft: '15px',
        // for some reason without this the buttons horizontal padding is overlapped by the text
        minWidth: 'auto',
        [theme.breakpoints.down('md')]: {
          margin: '10px auto 0 auto'
        }
      }
    },
    bookDemoText: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      fontWeight: 500,
      textDecoration: 'none'
    },
    backLink: {
      color: theme.paintscout.palette.brand.Neutral.P30,
      '& svg': {
        fontSize: '1em !important'
      },
      '@media print': {
        display: 'none'
      }
    },
    switcher: {
      backgroundColor: theme.paintscout.palette.primary,
      color: theme.palette.common.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: SPACE_BETWEEN,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    switcherContainer: {
      ...pageMargins(theme, 'xl'),
      display: 'flex',
      justifyContent: SPACE_BETWEEN,
      paddingLeft: -theme.spacing(2),
      paddingRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        justifyContent: SPACE_BETWEEN
      }
    },
    corporateBackLink: {
      marginRight: theme.spacing(8),
      marginLeft: -theme.spacing(1.5),
      fontSize: theme.typography.pxToRem(18),
      paddingLeft: 0,
      color: theme.palette.common.white,
      [theme.breakpoints.down('lg')]: {
        marginLeft: -theme.spacing(1)
      },
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(10)
      },
      [theme.breakpoints.down('sm')]: {
        marginLeft: -theme.spacing(1),
        marginRight: theme.spacing(0)
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0
      }
    },
    itemDiv: {
      display: 'flex',
      alignItems: 'center'
    },
    locationDropdown: {
      marginLeft: theme.spacing(1),
      maxHeight: theme.spacing(5),
      width: '500px',
      [theme.breakpoints.down('sm')]: {
        width: '300px'
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0
      },
      [theme.breakpoints.down(350)]: {
        width: '280px'
      }
    }
  }),
  { name: 'Header' }
)

function Header() {
  const { options } = useClientOptions()
  const features = getFeatures({ options })

  const {
    isTrial,
    trialExpired,
    isSubscribed,
    isSuperadmin,
    isSupport,
    paymentExpiryNotice,
    preferences,
    isCorporate,
    isMasquerading,
    isChurned
  } = useUser()
  const { permissionToUpdateBilling } = useUserPermissions()
  const location = useLocation()
  const classes = useStyles({ isMasquerading })
  const hasProfileCommunications = !getFeature({
    options,
    preferences,
    path: 'settingsCommunications.enabled'
  })
  const isBillingPage = location.pathname.startsWith('/settings')
  const showPaymentExpiryNotice =
    paymentExpiryNotice && isSubscribed && (!isSuperadmin || !isSupport) && features?.billing?.enabled
  const showInactiveBanner = !isSubscribed && (!isSuperadmin || !isSupport) && !(isChurned && isBillingPage)

  const showFollowUpErrorBanner = hasProfileCommunications
    ? Object.values(preferences?.emails?.followUps?.values ?? {}).filter((followUp) => {
        const smsMessage = striptags(followUp?.smsMessage ?? '')
        return followUp.sendText && (smsMessage.length === 0 || smsMessage.length > 95)
      }).length > 0
    : Object.values(options.options?.emails?.followUps?.values ?? {}).filter((followUp) => {
        const smsMessage = striptags(followUp?.smsMessage ?? '')
        return followUp.sendText && (smsMessage.length === 0 || smsMessage.length > 95)
      }).length > 0 && permissionToUpdateBilling

  const hideNewButtonForTrialDashboard = isTrial && trialExpired

  return (
    <>
      {/* Only show TrialBanner if trial is active (not expired) - expired trials show dedicated dashboards */}
      {isTrial && !trialExpired && <TrialBanner classes={classes} />}
      <MainHeader
        classes={classes}
        isCorporate={isCorporate}
        isMasquerading={isMasquerading}
        hideNewButtonForTrialDashboard={hideNewButtonForTrialDashboard}
      />
      {showInactiveBanner && <InactiveBanner classes={classes} />}
      {showPaymentExpiryNotice && <PaymentExpiryBanner classes={classes} />}
      {showFollowUpErrorBanner && (
        <FollowUpErrorBanner classes={classes} hasProfileCommunications={hasProfileCommunications} />
      )}
      <MultiTabChecker />
    </>
  )
}

interface HeaderProps {
  classes: any
  isCorporate: boolean
  isMasquerading: boolean
  hideNewButtonForTrialDashboard: boolean
}

export function shouldRenderNewButton({
  hideNewButtonForTrialDashboard,
  isCorporate,
  isMasquerading
}: {
  hideNewButtonForTrialDashboard: boolean
  isCorporate: boolean
  isMasquerading: boolean
}) {
  return !hideNewButtonForTrialDashboard && !(isCorporate && !isMasquerading)
}

function MainHeader(props: HeaderProps) {
  const { isCorporate, isMasquerading, classes, hideNewButtonForTrialDashboard } = props
  const { setOpen, pageSubtitle, pageChip } = useNavContext()
  const xsDown = useMediaQuery('xs')
  const location = useLocation()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const shouldShowNewButton = shouldRenderNewButton({
    hideNewButtonForTrialDashboard,
    isCorporate,
    isMasquerading
  })

  const pathToTitleMap: [string, string][] = [
    ['/dashboard', 'Dashboard'],
    ['/deals/edit', 'Deal'],
    ['/deals', 'Pipeline'],
    ['/quotes/edit', objectLabels.quote.value],
    ['/quotes', objectLabels.quote.plural],
    ['/invoices/edit', 'Invoice'],
    ['/invoices', 'Invoices'],
    ['/tasks/', 'Tasks'],
    ['/events', 'Calendar'],
    ['/events/?tab=calendar', 'Calendar'],
    ['/events/?tab=map', 'Calendar'],
    ['/scheduling/?tab=calendar', 'Calendar'],
    ['/scheduling/?tab=map', 'Calendar'],
    ['/people/contacts', 'Contacts'],
    ['/people/contact', 'Contact'],
    ['/people/companies', 'Companies'],
    ['/people/companies/*', 'Company'],
    ['/reports', 'Reports'],
    ['/locations', objectLabels.locations.plural],
    ['/integrate', 'Integrate'],
    ['/settings', 'Settings'],
    ['/profile', 'Profile']
  ]

  const pathToNewButtonMap: [string, string][] = [
    ['/dashboard/', 'all'],
    ['/deals/board', 'deal'],
    ['/deals/list', 'deal'],
    ['/quotes/', 'quote'],
    ['/invoices/', 'invoice'],
    ['/events/', 'event'],
    ['/tasks/', 'task'],
    ['/people/contacts/', 'contact'],
    ['/people/companies/', 'company']
  ]

  const newButtonElement = shouldShowNewButton ? renderNewButton(location.pathname) : null

  function getFriendlyTitle(pathname: string) {
    const match = pathToTitleMap.reduce<[string, string] | null>((best, entry) => {
      const [pattern] = entry
      const matches = pattern.includes('*')
        ? // eslint-disable-next-line security/detect-non-literal-regexp
          new RegExp('^' + pattern.replace(/\*/g, '.+')).test(pathname)
        : pathname.startsWith(pattern)
      if (matches && (!best || pattern.length > best[0].length)) {
        return entry
      }
      return best
    }, null)
    const title = match ? match[1] : ''

    return pageSubtitle ? (
      <Typography variant={'h4'}>
        <span style={{ fontWeight: 600 }}>{title}:</span> {` ${pageSubtitle}`}
      </Typography>
    ) : (
      <Typography variant={'h4'} style={{ fontWeight: 600 }}>
        {title}
      </Typography>
    )
  }

  function renderNewButton(pathname: string) {
    const cleanPath = pathname.split('?')[0].replace(/\/+$/, '') // remove trailing slashes
    // eslint-disable-next-line security/detect-non-literal-regexp
    const match = pathToNewButtonMap.find(([path]) => new RegExp(`^${path.replace(/\/+$/, '')}/?$`).test(cleanPath))

    if (!match) return null
    if (match[1] === 'all') {
      return <DropdownNewButton />
    } else {
      return <DropdownNewButton onlyFor={match[1]} />
    }
  }

  return (
    <header className={classes.root}>
      {isCorporate && isMasquerading && <CorporateMasqueradeSwitcher />}
      {!isCorporate && isMasquerading && <MasqueradeSwitcher />}
      <div className={classes.headerContentWrapper}>
        <div className={classes.headerContent}>
          <div className={classes.headerContentChild}>
            <Hidden lgUp>
              <IconButton
                classes={{ root: classes.drawerButton }}
                edge={xsDown ? null : 'start'}
                onClick={() => setOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
            <Hidden smDown>{getFriendlyTitle(`${location.pathname}${location.search}`)}</Hidden>
            {pageChip && <FeatureChip {...pageChip} collapseSize={'sm'} />}
            <QuoteBackLink />
          </div>
          <div className={classes.headerContentChild}>
            {newButtonElement}
            <UserButtons classes={{ root: classes.userButtons }} />
          </div>
        </div>
      </div>
    </header>
  )
}

function MasqueradeSwitcher() {
  const { isMasquerading } = useUser()
  const classes = useStyles({ isMasquerading })
  const history = useHistory()

  return (
    <div className={classes.switcher}>
      <Grid container spacing={1} className={classes.switcherContainer}>
        <Grid item className={classes.itemDiv} xs={12} sm={6} md={4} style={{ marginLeft: '-10px' }}>
          <Button
            classes={{ label: classes.corporateBackLink }}
            variant={'text'}
            onClick={() => {
              window.localStorage.removeItem('masqueradeCompanyId')
              history.push('/')
              window.location.reload()
            }}
            startIcon={<CancelIcon />}
          >
            <Typography variant={'body1'} style={{ textTransform: 'none' }}>
              Stop Masquerading
            </Typography>
          </Button>
        </Grid>
      </Grid>
    </div>
  )
}

function CorporateMasqueradeSwitcher() {
  const { isMasquerading } = useUser()
  const classes = useStyles({ isMasquerading })
  const history = useHistory()

  const masqueradeCompanyId = window.localStorage.getItem('masqueradeCompanyId')

  const { data, error: _error } = useSearchLocationsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: '*:*'
    }
  })

  if (!masqueradeCompanyId) {
    return null
  }

  const locations = (data?.searchLocations?.rows ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((row) => {
      return {
        label: row.name,
        value: row._id
      }
    })

  return (
    <div className={classes.switcher}>
      <Grid container spacing={1} className={classes.switcherContainer}>
        <Grid item className={classes.itemDiv} xs={12} sm={6} md={4} style={{ marginLeft: '-5px' }}>
          <Button
            classes={{ label: classes.corporateBackLink }}
            variant={'text'}
            onClick={async () => {
              window.localStorage.removeItem('masqueradeCompanyId')
              history.push('/')
            }}
            startIcon={<ArrowBackIcon />}
          >
            <Typography variant={'body1'} style={{ textTransform: 'none' }}>
              Back to Corporate Dashboard
            </Typography>
          </Button>
        </Grid>
        <Grid item className={classes.itemDiv}>
          <Hidden xsDown>
            <Typography variant={'body1'}>Viewing:</Typography>
          </Hidden>
          <DropdownSelect
            className={classes.locationDropdown}
            options={locations}
            value={find(locations, { value: masqueradeCompanyId })}
            variant={'single'}
            onChange={async (option) => {
              if (option.value) {
                window.localStorage.setItem('masqueradeCompanyId', option.value)
                history.push('/')
              }
            }}
          />
        </Grid>
      </Grid>
    </div>
  )
}

function QuoteBackLink() {
  const { isMasquerading } = useUser()
  const classes = useStyles({ isMasquerading })
  // const { options } = useClientOptions()
  // const objectLabels = getObjectLabels({ options })
  const history = useHistory()
  const { hideBackLink } = useNavContext()

  const getLink = (_pathname: string) => {
    // if (!pathname) {
    //   return 'Back'
    // } else if (pathname.includes('/quotes/edit') || pathname.includes('/quotes/new')) {
    //   return `Back to ${objectLabels.quote.value}`
    // } else if (pathname.includes('/quotes')) {
    //   return `Back to ${objectLabels.quote.plural}`
    // } else if (pathname.includes('/invoices/edit') || pathname.includes('/invoices/new')) {
    //   return 'Back to Invoice'
    // } else if (pathname.includes('/invoices')) {
    //   return 'Back to Invoices'
    // } else if (pathname.includes('/settings')) {
    //   return 'Back to Settings'
    // } else if (pathname.includes('/people/contact')) {
    //   return 'Back to Contact'
    // } else if (pathname.includes('/people')) {
    //   return 'Back to People'
    // }

    return 'Back'
  }

  const showLink =
    !hideBackLink &&
    ['/quotes/edit', '/quotes/new', '/invoices/edit', '/invoices/new'].some((link) =>
      history.location.pathname.includes(link)
    )

  if (showLink) {
    return (
      <Hidden mdUp>
        <BackLink>
          {(prevLocation) => (
            <Button className={classes.backLink} variant="text" size="small" startIcon={<ArrowBackIcon />}>
              {getLink(prevLocation?.pathname)}
            </Button>
          )}
        </BackLink>
      </Hidden>
    )
  }
  return null
}

function FollowUpErrorBanner(props: { classes: any; hasProfileCommunications: boolean }) {
  const { classes, hasProfileCommunications } = props

  return (
    <div className={classes.paymentBannerWrapper}>
      <div className={classnames(classes.paymentBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Hidden mdDown>
          <ErrorOutlineIcon />
        </Hidden>
        <div>
          <Typography variant="body1">
            We’ve updated our SMS follow-up requirements, and some of your messages need adjustment.
          </Typography>
          {/* <br /> */}
          <Typography variant="body1">
            <>
              Please click
              <Link
                style={{ color: 'red' }}
                to={
                  hasProfileCommunications
                    ? '/profile?category=follow-ups'
                    : '/settings?section=communications-follow-ups'
                }
              >
                {' '}
                here
              </Link>{' '}
              to apply fixes.
            </>
          </Typography>
        </div>
      </div>
    </div>
  )
}

function PaymentExpiryBanner(props: { classes: any }) {
  const { permissionToUpdateBilling } = useUserPermissions()
  const { classes } = props

  return (
    <div className={classes.paymentBannerWrapper}>
      <div className={classnames(classes.paymentBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Hidden mdDown>
          <ErrorOutlineIcon />
        </Hidden>
        {!permissionToUpdateBilling ? (
          <Typography variant="body1">
            <>Your payment method is expiring soon. Please contact your admin user.</>
          </Typography>
        ) : (
          <Typography variant="body1">
            <>
              Your payment method is expiring soon. Please update your payment method{' '}
              <Link style={{ color: 'red' }} to={BILLING_PLAN_PATH}>
                {' '}
                here
              </Link>
            </>
          </Typography>
        )}
      </div>
    </div>
  )
}

function TrialBanner(props: { classes: any }) {
  const { trialExpired, trialExpiry, updateTrialExpired, user } = useUser()
  const { permissionToUpdateBilling } = useUserPermissions()
  const leadSource = user.app_metadata?.company?.leadSource || ''
  const mobile = useIsMobile()
  const { crmVisible } = useFlags()
  const { classes } = props
  moment.relativeTimeThreshold('h', 49)
  const timeRemaining = moment(trialExpiry).fromNow(true)
  const showSwCoupon = leadSource === 'Sherwin Williams'
  const couponMessage = showSwCoupon
    ? `${mobile ? '\n' : ' '}Select your plan to claim your $100 Sherwin-Williams coupon.`
    : ''

  // feeds the current time on each render to UserProvider
  // so it can set trialExpired accurately and send it throughout the app
  useEffect(() => {
    updateTrialExpired(Date.now())
  })

  function getTrialMessage() {
    if (trialExpired) {
      return permissionToUpdateBilling
        ? `Your trial has ended. ${couponMessage ? '' : 'Select a plan to get started.'}${couponMessage}`
        : `Your trial has ended. Contact an admin on your team to get started.`
    }

    return `You have ${timeRemaining} left on your PaintScout${crmVisible ? ' Sales + Operations' : ''} trial.${couponMessage}`
  }

  return (
    <div className={classes.trialBannerWrapper}>
      <div className={classnames(classes.trialBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
          {getTrialMessage()}
        </Typography>
        {permissionToUpdateBilling && (
          <div className={classes.trialBannerActions}>
            <Link to={BILLING_PLAN_PATH} tabIndex={-1}>
              <Button variant={'dialog'}>Select Plan</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

function InactiveBanner(props: { classes: any }) {
  const { classes } = props
  const { permissionToUpdateBilling } = useUserPermissions()

  return (
    <div className={classes.trialBannerWrapper}>
      <div className={classnames(classes.trialBanner, classes.banner, TRIAL_BANNER_CLASS)}>
        <Typography variant="body1">Your subscription is currently inactive.</Typography>
        {permissionToUpdateBilling && (
          <Link to={BILLING_PLAN_PATH} tabIndex={-1}>
            <Button disableElevation variant="dialog">
              Select Plan
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

declare global {
  interface Window {
    Intercom: any
  }
}

export default Header
